import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Camera,
  X,
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  Send,
  ClipboardCheck,
  Wrench as WrenchIcon,
  Package,
  Plus,
  Trash2,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAutoSave } from "@/lib/useAutoSave";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SignaturePadField from "@/components/engineer/SignaturePadField";

function fmtDur(ms) {
  if (ms == null || ms < 0) return "0:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
}

export default function ExecuteTab({ job, onChange }) {
  const navigate = useNavigate();
  const [machinesReport, setMachinesReport] = useState(() => initReport(job));
  const [actionsRequired, setActionsRequired] = useState(job.actions_required || false);
  const [actionsNotes, setActionsNotes] = useState(job.execution?.actions_required_notes || "");
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState([]);
  const [checklistDefaults, setChecklistDefaults] = useState({
    require_photo_on_issue: false,
    require_notes_on_issue: false,
  });

  // Fetch checklist defaults once — used to surface inline P7 hints.
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/checklist-defaults").then(({ data }) => {
      if (!cancelled) setChecklistDefaults({
        require_photo_on_issue: !!data.require_photo_on_issue,
        require_notes_on_issue: !!data.require_notes_on_issue,
      });
    }).catch(() => { /* silent — hints are cosmetic */ });
    return () => { cancelled = true; };
  }, []);

  const photosByMachine = useMemo(() => {
    const m = {};
    for (const p of (job.execution?.photos || [])) {
      if (p.machine_id) m[p.machine_id] = (m[p.machine_id] || 0) + 1;
    }
    return m;
  }, [job.execution?.photos]);

  // Merge report shape when machine set changes
  useEffect(() => { setMachinesReport(initReport(job)); }, [job.id]);
  useEffect(() => { setActionsRequired(job.actions_required || false); }, [job.id, job.actions_required]);
  useEffect(() => { setActionsNotes(job.execution?.actions_required_notes || ""); }, [job.id, job.execution?.actions_required_notes]);

  const patchExecution = useCallback(async (value) => {
    await api.patch(`/jobs/${job.id}/execution`, {
      machines_report: value.machines_report,
      actions_required_notes: value.actions_required_notes || null,
    });
  }, [job.id]);

  const autosaveValue = useMemo(() => ({
    machines_report: machinesReport,
    actions_required_notes: actionsNotes,
  }), [machinesReport, actionsNotes]);

  const { status: saveStatus, flush } = useAutoSave(autosaveValue, patchExecution, { delay: 15000 });

  const toggleActions = async (next) => {
    setActionsRequired(next);
    try {
      await api.post(`/jobs/${job.id}/actions-required`, { actions_required: next, notes: next ? actionsNotes : null });
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); setActionsRequired(!next); }
  };

  const submit = async () => {
    setSubmitting(true);
    setIssues([]);
    try {
      // Save before submit
      await flush();
      // Persist actions_required if user toggled without notes save
      if (actionsRequired) {
        await api.post(`/jobs/${job.id}/actions-required`, { actions_required: true, notes: actionsNotes });
      }
      await api.post(`/jobs/${job.id}/complete`);
      toast.success("Job submitted");
      onChange && onChange();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Bug 8e handoff: second /complete on an already-completed / closed job.
      if (status === 409 && typeof detail === "string" && /already completed|closed/i.test(detail)) {
        toast.info("This job was already submitted. Opening the read-only report.");
        navigate(`/my/jobs/${job.id}/report`, { replace: true });
        return;
      }
      if (detail && typeof detail === "object" && Array.isArray(detail.issues)) {
        setIssues(detail.issues);
        toast.error(`Not ready to submit — ${detail.issues.length} issue(s)`);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="engineer-execute-tab">
      <TimeOnSite job={job} onChange={onChange} />

      <div className="space-y-4">
        {(job.machines || []).map((m, mi) => (
          <MachinePanel
            key={m.id}
            machine={m}
            report={machinesReport[mi]}
            onChange={(next) => setMachinesReport((prev) => prev.map((r, i) => (i === mi ? next : r)))}
            checklistDefaults={checklistDefaults}
            photoCount={photosByMachine[m.id] || 0}
          />
        ))}
      </div>

      <PhotosSection job={job} onChange={onChange} />

      <ActionsRequiredCard
        actionsRequired={actionsRequired}
        actionsNotes={actionsNotes}
        onToggle={toggleActions}
        onNotesChange={setActionsNotes}
      />

      <SignaturesCard job={job} onChange={onChange} />

      {issues.length > 0 && (
        <Card className="rounded-md border-destructive" data-testid="engineer-submit-issues">
          <CardHeader><CardTitle className="text-sm font-medium text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Fix these before submitting</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            {issues.map((it, i) => <div key={i} className="text-destructive" data-testid={`engineer-submit-issue-${i}`}>• {it.message}</div>)}
          </CardContent>
        </Card>
      )}

      {/* Sticky submit bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-56 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex-1 flex items-center gap-2 text-xs" data-testid="engineer-autosave-status">
            {saveStatus === "saving" && (<><Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /><span className="text-muted-foreground">Saving…</span></>)}
            {saveStatus === "saved" && (<><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /><span className="text-muted-foreground">Saved just now</span></>)}
            {saveStatus === "error" && (<><AlertTriangle className="h-3.5 w-3.5 text-destructive" /><span className="text-destructive">Save failed — retrying</span></>)}
            {saveStatus === "idle" && <span className="text-muted-foreground">Auto-save every 15s</span>}
          </div>
          <Button size="lg" className="min-h-12 gap-2" onClick={submit} disabled={submitting} data-testid="engineer-submit-job">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" />Submitting…</> : <><Send className="h-4 w-4" />Submit Job</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Time on site ----

function TimeOnSite({ job, onChange }) {
  const entries = job.execution?.time_entries || [];
  const openEntry = entries.length && !entries[entries.length - 1].left_at ? entries[entries.length - 1] : null;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!openEntry) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [openEntry]);

  const cumulative = entries.reduce((acc, e) => {
    const start = new Date(e.arrived_at).getTime();
    const end = e.left_at ? new Date(e.left_at).getTime() : Date.now();
    return acc + Math.max(0, end - start);
  }, 0);

  const arrive = async () => {
    try {
      await api.post(`/jobs/${job.id}/timer`, { action: "arrive" });
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const leave = async () => {
    try {
      await api.post(`/jobs/${job.id}/timer`, { action: "leave" });
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [manualOpen, setManualOpen] = useState(false);
  const [manArrived, setManArrived] = useState("");
  const [manLeft, setManLeft] = useState("");
  const submitManual = async () => {
    if (!manArrived || !manLeft) return toast.error("Both times required");
    try {
      const arrivedIso = new Date(manArrived).toISOString();
      const leftIso = new Date(manLeft).toISOString();
      await api.post(`/jobs/${job.id}/timer`, { arrived_at: arrivedIso, left_at: leftIso });
      setManualOpen(false); setManArrived(""); setManLeft("");
      onChange && onChange();
      toast.success("Manual entry added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card className="rounded-md" data-testid="engineer-timer-card" data-tick={tick}>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Time on site</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-2xl font-bold font-mono" data-testid="engineer-timer-display">{fmtDur(cumulative)}</div>
          {openEntry ? (
            <Button size="lg" variant="outline" className="min-h-11 gap-2 border-destructive text-destructive" onClick={leave} data-testid="engineer-timer-leave">
              <Pause className="h-4 w-4" /> Leave
            </Button>
          ) : (
            <Button size="lg" className="min-h-11 gap-2" onClick={arrive} data-testid="engineer-timer-arrive">
              <Play className="h-4 w-4" /> Arrive
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setManualOpen((v) => !v)} data-testid="engineer-timer-manual-toggle">
            {manualOpen ? "Cancel manual" : "Add manual entry"}
          </Button>
        </div>
        {manualOpen && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end border border-border rounded-sm p-3">
            <div className="space-y-1"><Label className="text-xs">Arrived</Label><Input type="datetime-local" value={manArrived} onChange={(e) => setManArrived(e.target.value)} data-testid="engineer-timer-manual-arrived" /></div>
            <div className="space-y-1"><Label className="text-xs">Left</Label><Input type="datetime-local" value={manLeft} onChange={(e) => setManLeft(e.target.value)} data-testid="engineer-timer-manual-left" /></div>
            <Button className="min-h-11" onClick={submitManual} data-testid="engineer-timer-manual-submit">Add entry</Button>
          </div>
        )}
        {entries.length > 0 && (
          <div className="space-y-1 text-xs">
            <div className="font-bold uppercase tracking-wider text-muted-foreground">Sessions</div>
            <ul className="space-y-1" data-testid="engineer-timer-sessions">
              {entries.map((e, i) => (
                <li key={i} className="flex items-center gap-2" data-testid={`engineer-timer-session-${i}`}>
                  <span>{new Date(e.arrived_at).toLocaleString()}</span>
                  <span className="text-muted-foreground">→</span>
                  <span>{e.left_at ? new Date(e.left_at).toLocaleString() : <span className="text-primary font-medium">now</span>}</span>
                  <span className="text-muted-foreground">· {e.source}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Per-machine panel ----

function initReport(job) {
  const existing = job.execution?.machines_report || [];
  const byMachine = Object.fromEntries(existing.map((r) => [r.machine_id, r]));
  return (job.machines || []).map((m) => {
    const template = m.machine_type?.default_checklist || [];
    const stored = byMachine[m.id];
    const responses = template.map((item) => {
      const prev = stored?.checklist_responses?.find((r) => r.item_id === item.id);
      return prev || {
        item_id: item.id,
        label_snapshot: item.label,
        kind: item.kind,
        value: null,
        notes: null,
      };
    });
    return {
      machine_id: m.id,
      checklist_responses: responses,
      parts_used: stored?.parts_used || [],
      recommendations: stored?.recommendations || "",
    };
  });
}

function MachinePanel({ machine, report, onChange, checklistDefaults, photoCount }) {
  const template = machine.machine_type?.default_checklist || [];
  const parts_picklist = machine.machine_type?.default_parts_picklist || [];

  const setResponse = (item_id, patch) => {
    onChange({
      ...report,
      checklist_responses: report.checklist_responses.map((r) => (r.item_id === item_id ? { ...r, ...patch } : r)),
    });
  };

  const addPart = () => {
    onChange({ ...report, parts_used: [...report.parts_used, { from_picklist: false, sku: null, name: "", qty: 1, unit: null }] });
  };
  const updatePart = (i, patch) => {
    onChange({ ...report, parts_used: report.parts_used.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });
  };
  const removePart = (i) => {
    onChange({ ...report, parts_used: report.parts_used.filter((_, idx) => idx !== i) });
  };

  return (
    <Card className="rounded-md" data-testid={`engineer-machine-panel-${machine.id}`}>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <WrenchIcon className="h-4 w-4 text-primary" />
          {machine.make} {machine.model}
          <span className="text-xs text-muted-foreground font-normal">· S/N {machine.serial} · {machine.machine_type?.name}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Checklist */}
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Checklist</div>
            {template.length === 0 && <div className="text-sm text-muted-foreground italic">No checklist configured.</div>}
            {report.checklist_responses.map((r, i) => (
              <ChecklistRow
                key={r.item_id}
                item={template[i]}
                response={r}
                onChange={(patch) => setResponse(r.item_id, patch)}
                machineId={machine.id}
                checklistDefaults={checklistDefaults}
                photoCount={photoCount}
              />
            ))}
          </div>
          {/* Parts */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Parts used</div>
              <Button size="sm" variant="outline" className="min-h-8 gap-1" onClick={addPart} data-testid={`engineer-parts-add-${machine.id}`}>
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
            {report.parts_used.length === 0 && <div className="text-xs text-muted-foreground italic">No parts.</div>}
            {report.parts_used.map((p, i) => (
              <PartRow
                key={i}
                index={i}
                part={p}
                picklist={parts_picklist}
                onChange={(patch) => updatePart(i, patch)}
                onRemove={() => removePart(i)}
                machineId={machine.id}
              />
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Recommendations</Label>
          <Textarea
            value={report.recommendations}
            onChange={(e) => onChange({ ...report, recommendations: e.target.value })}
            rows={3}
            placeholder="Notes for the customer / office / next visit"
            data-testid={`engineer-recommendations-${machine.id}`}
            className="text-base"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ChecklistRow({ item, response, onChange, machineId, checklistDefaults, photoCount }) {
  const tid = `engineer-checklist-${machineId}-${item?.id || "unknown"}`;
  const [notesOpen, setNotesOpen] = useState(Boolean(response.notes));
  if (!item) return null;
  const req = item.required ? <span className="text-destructive">*</span> : null;

  const isOkIssue = item.kind === "ok_issue";
  const flaggedIssue = isOkIssue && response.value === "issue";
  const defaults = checklistDefaults || {};
  const needPhoto = flaggedIssue && defaults.require_photo_on_issue && !(photoCount > 0);
  const needNotes = flaggedIssue && defaults.require_notes_on_issue && !(response.notes && String(response.notes).trim());
  const showNotesRow = notesOpen || needNotes;

  return (
    <div className="border border-border rounded-sm p-3 space-y-2" data-testid={tid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm">{item.label} {req}</div>
        <button
          type="button"
          onClick={() => setNotesOpen((v) => !v)}
          className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          data-testid={`${tid}-notes-toggle`}
        >
          {notesOpen ? "Hide note" : "Add note"}
        </button>
      </div>
      {item.kind === "yes_no" && (
        <div className="flex gap-2">
          <Button size="sm" variant={response.value === true ? "default" : "outline"} className="min-h-11 flex-1" onClick={() => onChange({ value: true })} data-testid={`${tid}-yes`}>
            <Check className="h-4 w-4 mr-1" /> Yes
          </Button>
          <Button size="sm" variant={response.value === false ? "default" : "outline"} className="min-h-11 flex-1" onClick={() => onChange({ value: false })} data-testid={`${tid}-no`}>
            <X className="h-4 w-4 mr-1" /> No
          </Button>
        </div>
      )}
      {item.kind === "ok_issue" && (
        <div className="flex gap-2">
          <Button size="sm" variant={response.value === "ok" ? "default" : "outline"} className="min-h-11 flex-1" onClick={() => onChange({ value: "ok" })} data-testid={`${tid}-ok`}>
            OK
          </Button>
          <Button size="sm" variant={response.value === "issue" ? "destructive" : "outline"} className="min-h-11 flex-1" onClick={() => onChange({ value: "issue" })} data-testid={`${tid}-issue`}>
            Issue
          </Button>
        </div>
      )}
      {item.kind === "numeric" && (
        <Input
          type="number"
          value={response.value ?? ""}
          onChange={(e) => onChange({ value: e.target.value === "" ? null : Number(e.target.value) })}
          className="text-base min-h-11"
          data-testid={`${tid}-value`}
        />
      )}
      {item.kind === "text" && (
        <Textarea
          value={response.value ?? ""}
          onChange={(e) => onChange({ value: e.target.value })}
          rows={2}
          className="text-base"
          data-testid={`${tid}-value`}
        />
      )}
      {showNotesRow && (
        <Input
          placeholder="Row note"
          value={response.notes ?? ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          className="text-sm"
          data-testid={`${tid}-notes`}
        />
      )}
      {flaggedIssue && (needPhoto || needNotes) && (
        <div className="rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-800 dark:text-amber-300 text-[11px] px-2 py-1.5 flex items-start gap-1.5" data-testid={`${tid}-issue-hints`}>
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            {needPhoto && <div data-testid={`${tid}-hint-photo`}>Attach a photo for this machine before submitting.</div>}
            {needNotes && <div data-testid={`${tid}-hint-notes`}>Add a row note explaining the issue before submitting.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function PartRow({ index, part, picklist, onChange, onRemove, machineId }) {
  const tid = `engineer-part-${machineId}-${index}`;
  return (
    <div className="grid grid-cols-[1fr_80px_80px_auto] gap-2 items-center" data-testid={tid}>
      <select
        value={part.from_picklist ? part.sku || "" : "__custom__"}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__custom__") {
            onChange({ from_picklist: false, sku: null, name: "", unit: null });
          } else {
            const pick = picklist.find((p) => p.sku === v);
            if (pick) onChange({ from_picklist: true, sku: pick.sku, name: pick.name, unit: pick.unit });
          }
        }}
        className="h-10 rounded-sm border border-border bg-background px-2 text-sm"
        data-testid={`${tid}-select`}
      >
        <option value="__custom__">Custom part…</option>
        {picklist.map((p) => (<option key={p.sku} value={p.sku}>{p.sku} — {p.name}</option>))}
      </select>
      {!part.from_picklist && (
        <Input placeholder="Part name" value={part.name} onChange={(e) => onChange({ name: e.target.value })} className="col-span-1 h-10 text-sm min-w-0" data-testid={`${tid}-name`} />
      )}
      {part.from_picklist && <div className="text-xs text-muted-foreground truncate">{part.name}</div>}
      <Input type="number" min="0" value={part.qty} onChange={(e) => onChange({ qty: Number(e.target.value || 0) })} className="h-10 text-sm text-center" data-testid={`${tid}-qty`} />
      <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`${tid}-remove`} className="h-10 w-10">
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );
}

// ---- Photos ----

function PhotosSection({ job, onChange }) {
  const photos = job.execution?.photos || [];
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > 20) return toast.error("Max 20 photos");
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        form.append("kind", "photo");
        const { data: f } = await api.post("/files", form, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/jobs/${job.id}/photos`, { file_id: f.id });
      }
      onChange && onChange();
      toast.success(`${files.length} photo(s) added`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async (file_id) => {
    try {
      await api.delete(`/jobs/${job.id}/photos/${file_id}`);
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card className="rounded-md" data-testid="engineer-photos-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2"><Camera className="h-4 w-4 text-primary" /> Photos</CardTitle>
        <span className="text-xs text-muted-foreground" data-testid="engineer-photos-count">{photos.length}/20</span>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.file_id} className="relative group border border-border rounded-sm overflow-hidden bg-muted" data-testid={`engineer-photo-${p.file_id}`}>
              <img
                src={`/api/files/${p.file_id}`}
                alt={p.caption || "photo"}
                className="w-full aspect-square object-cover"
                onError={(e) => { e.target.style.opacity = 0.2; }}
              />
              <button
                type="button"
                onClick={() => remove(p.file_id)}
                className="absolute top-1 right-1 h-8 w-8 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`engineer-photo-remove-${p.file_id}`}
                aria-label="Remove photo"
              >
                <X className="h-4 w-4" />
              </button>
              {p.caption && <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{p.caption}</div>}
            </div>
          ))}
          {photos.length < 20 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border border-dashed border-border rounded-sm aspect-square flex flex-col items-center justify-center gap-1 hover:border-primary hover:text-primary text-muted-foreground min-h-[120px]"
              data-testid="engineer-photo-add"
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-6 w-6" />}
              <span className="text-xs">{uploading ? "Uploading…" : "Add photo"}</span>
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onFilePick}
          className="hidden"
          data-testid="engineer-photo-input"
        />
      </CardContent>
    </Card>
  );
}

// ---- Actions required ----

function ActionsRequiredCard({ actionsRequired, actionsNotes, onToggle, onNotesChange }) {
  return (
    <Card className="rounded-md" data-testid="engineer-actions-required-card">
      <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Actions required for office</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <Button size="lg" variant={actionsRequired ? "default" : "outline"} className="min-h-11 flex-1" onClick={() => onToggle(true)} data-testid="engineer-actions-yes">
            Yes — Follow-up needed
          </Button>
          <Button size="lg" variant={!actionsRequired ? "default" : "outline"} className="min-h-11 flex-1" onClick={() => onToggle(false)} data-testid="engineer-actions-no">
            No — All clear
          </Button>
        </div>
        {actionsRequired && (
          <div className="space-y-1">
            <Label className="text-xs">Notes for office <span className="text-destructive">*</span></Label>
            <Textarea
              value={actionsNotes}
              onChange={(e) => onNotesChange(e.target.value)}
              rows={3}
              placeholder="What does the office need to do?"
              className="text-base"
              data-testid="engineer-actions-notes"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Signatures ----

function SignaturesCard({ job, onChange }) {
  const eng = job.execution?.signatures?.engineer;
  const cust = job.execution?.signatures?.customer;

  return (
    <Card className="rounded-md" data-testid="engineer-signatures-card">
      <CardHeader><CardTitle className="text-sm font-medium">Signatures</CardTitle></CardHeader>
      <CardContent className="grid gap-4 lg:grid-cols-2">
        <SignaturePadField
          testIdPrefix="engineer-sig-engineer"
          label="Engineer signature"
          existing={eng}
          onSave={async ({ file_id, name }) => {
            await api.post(`/jobs/${job.id}/signatures/engineer`, { file_id, name });
            onChange && onChange();
          }}
        />
        <SignaturePadField
          testIdPrefix="engineer-sig-customer"
          label="Customer signature"
          existing={cust}
          allowRefused
          onSave={async ({ file_id, name }) => {
            await api.post(`/jobs/${job.id}/signatures/customer`, { file_id, name });
            onChange && onChange();
          }}
          onRefuse={async (reason) => {
            await api.post(`/jobs/${job.id}/signatures/customer`, { refused: true, reason });
            onChange && onChange();
          }}
        />
      </CardContent>
    </Card>
  );
}
